import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatchService } from './match.service';
import { DecimalPipe, DatePipe } from '@angular/common';
import { IsrcPipe } from '../pipes/isrc.pipe';

import { ElasticDialogGrid } from './elastic-dialog-grid.model';
import { ElasticDialogResponse } from './elastic-dialog-response.model';
import { DigitalMatch } from '../shared/digital-match';

@Component({
  selector: 'app-elastic-dialog',
  templateUrl: './elastic-dialog.component.html',
  styleUrls: ['./elastic-dialog.component.css']
})
export class ElasticDialogComponent implements OnInit {
  public matchMade: ElasticDialogResponse = null;
  public gridError = false;
  public itemsPerPage = 5;
  public matchColumnDefs;
  public musicPointColumnDefs;
  public getRowHeight;
  public selectedRow;
  public musicPointGridOptions = { alignedGrids: [] };
  public matchGridOptions = { alignedGrids: [] };

  private gridApi;
  public elasticResults: Array<DigitalMatch>;

  constructor(@Inject(MAT_DIALOG_DATA) public data,
    private matchService: MatchService,
    private decimalPipe: DecimalPipe,
    private datePipe: DatePipe,
    private isrcPipe: IsrcPipe,
    public dialogRef: MatDialogRef<ElasticDialogComponent>) { }


  ngOnInit() {
    this.musicPointColumnDefs = [
      {
        headerName: 'Spins',
        field: 'spins',
        width: 65,
        lockPosition: true
      },
      {
        headerName: 'CCID',
        field: 'ccidOrMatchRate',
        width: 75,
        lockPosition: true
      },
      {
        headerName: 'Title',
        field: 'title',
        width: 135,
        lockPosition: true
      },
      {
        headerName: 'Artist',
        field: 'artist',
        width: 140,
        lockPosition: true
      },
      {
        headerName: 'Album',
        field: 'album',
        width: 170,
        lockPosition: true
      },
      {
        headerName: 'Duration',
        field: 'duration',
        width: 85,
        lockPosition: true
      },
      {
        headerName: 'Version',
        field: 'version',
        width: 70,
        lockPosition: true
      },
      {
        headerName: 'Genre',
        field: 'genre',
        width: 70,
        lockPosition: true
      },
      {
        headerName: 'Label',
        field: 'label',
        width: 95,
        lockPosition: true
      },
      {
        headerName: 'ISRC',
        field: 'isrc',
        width: 125,
        cellRenderer: (params) => {
          return this.isrcPipe.transform(params.value);
        },
        lockPosition: true
      },
      {
        headerName: 'Created Date',
        field: 'date',
        width: 100,
        cellRenderer: (params) => {
          return this.datePipe.transform(params.value, 'yyyy-MM-dd hh:mm:ss');
        },
        lockPosition: true
      },
      {
        headerName: 'Updated Date',
        field: 'inRadioModel',
        width: 100,
        cellRenderer: (params) => {
          return this.datePipe.transform(params.value, 'yyyy-MM-dd hh:mm:ss');
        },
        lockPosition: true
      }
    ];

    this.matchColumnDefs = [
      {
        headerName: '',
        field: 'spins',
        width: 65,
        cellRenderer: (params) => '',
        sortingOrder: [null],
        pinned: 'left',
        checkboxSelection: true,
        lockPosition: true
      },
      {
        headerName: 'Match Rate',
        field: 'ccidOrMatchRate',
        width: 75,
        cellRenderer: (params) => {
          return this.decimalPipe.transform(params.value, '1.1-1');
        },
        lockPosition: true
      },
      {
        headerName: 'Title',
        field: 'title',
        width: 135,
        lockPosition: true
      },
      {
        headerName: 'Artist',
        field: 'artist',
        width: 140,
        lockPosition: true
      },
      {
        headerName: 'Album',
        field: 'album',
        width: 170,
        lockPosition: true
      },
      {
        headerName: 'Duration',
        field: 'duration',
        width: 85,
        lockPosition: true
      },
      {
        headerName: 'Version',
        field: 'version',
        width: 70,
        lockPosition: true
      },
      {
        headerName: 'Genre',
        field: 'genre',
        width: 70,
        lockPosition: true
      },
      {
        headerName: 'Label',
        field: 'label',
        width: 95,
        lockPosition: true
      },
      {
        headerName: 'ISRC',
        field: 'isrc',
        width: 125,
        cellRenderer: (params) => {
          return this.isrcPipe.transform(params.value);
        },
        lockPosition: true
      },
      {
        headerName: 'Date',
        field: 'date',
        width: 100,
        cellRenderer: (params) => {
          return this.datePipe.transform(params.value, 'yyyy-MM-dd hh:mm:ss');
        },
        lockPosition: true
      },
      {
        headerName: 'In Radio Model?',
        field: 'inRadioModel',
        width: 100,
        cellRenderer: (params) => {
          return params.value === '0' ? 'No' : 'Yes';
        },
        lockPosition: true
      }
    ];

    this.getRowHeight = function (params) {
      return 55;
    };

    this.musicPointGridOptions.alignedGrids.push(this.matchGridOptions);
    this.matchGridOptions.alignedGrids.push(this.musicPointGridOptions);
  }

  onMusicPointGridReady(params) {
    if (this.data) {
      const musicPointData = [{
        'spins': this.data.spins,
        'ccidOrMatchRate': this.data.ccid,
        'title': this.data.title,
        'artist': this.data.artist,
        'album': this.data.album,
        'duration': this.data.duration,
        'version': this.data.version,
        'label': this.data.label,
        'date': this.data.createdDate,
        'inRadioModel': this.data.updatedDate,
        'isrc': this.data.isrc,
        'genre': this.data.format
      }];

      params.api.setRowData(musicPointData);
    } else {
      params.api.showNoRowsOverlay();
    }
  }

  onMatchGridReady(params) {
    this.gridApi = params.api;
    this.gridError = false;

    this.matchService.getElasticResults(this.data.artist.toLowerCase(), this.data.title.toLowerCase()).subscribe(
      res => {
        if (res.length !== 0) {
          const matchData: ElasticDialogGrid[] = [];
          this.elasticResults = res;
          res.forEach((value, index) => {
            const elasticDialogGrid: ElasticDialogGrid = value as ElasticDialogGrid;

            elasticDialogGrid.spins = 0;
            elasticDialogGrid.ccidOrMatchRate = String(value.score);
            elasticDialogGrid.artist = value.artistName;
            elasticDialogGrid.album = value.trackBundleTitle;
            elasticDialogGrid.version = value.trackVersion;
            elasticDialogGrid.label = value.recordLabelName;
            elasticDialogGrid.date = value.releasedate;
            elasticDialogGrid.index = index;
            matchData.push(elasticDialogGrid);
          });

          this.gridApi.setRowData(matchData);
        } else {
          this.gridApi.showNoRowsOverlay();
        }

        this.gridApi.paginationGoToPage(0);
      },
      error => {
        this.gridApi.setRowData([]);
        this.gridApi.hideOverlay();
        this.gridError = true;
      });
  }

  changeItemsPerPage() {
    this.gridApi.paginationSetPageSize(this.itemsPerPage);
  }

  onSelectionChanged() {
    this.selectedRow = this.gridApi.getSelectedRows()[0];
  }

  saveMatch() {
    const elasticData = this.elasticResults[this.selectedRow.index];

    this.matchService.getMetadata(this.data.ccid).subscribe(metadata => {
      metadata.upc = elasticData.upc;
      metadata.collaborativeArtist = elasticData.collaborativeArtist;
      this.matchService.saveOriginalData(metadata).subscribe(metadataSave => {
        if (metadataSave) {
          this.matchService.saveDigitalMatch(elasticData, this.data).subscribe(res => {
            this.matchMade = {
              'isMatch': true,
              'message': 'Match successfully saved'
            };
            this.onClose();
          });
        }
      });
    });
  }

  saveNoMatch() {
    this.matchService.saveNoMatch(this.data).subscribe(res => {
      if (res) {
        this.matchMade = {
          'isMatch': true,
          'message': 'No Acceptable Match Confirmed'
        };
        this.onClose();
      }
    });
  }

  onClose(): void {
    this.dialogRef.close(this.matchMade);
  }

}



// WEBPACK FOOTER //
// ./src/app/match/elastic-dialog.component.ts